import React, { useEffect, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import RemoveIcon from '@mui/icons-material/Remove'
import CircularProgress from '@mui/material/CircularProgress'
import { NumericFormat } from 'react-number-format'
import { toast } from 'react-toastify'

import httpClient from '../helpers/httpClient'
import { formatPropertyIdentifier } from '../helpers/propertyIdentifier'


const PRICE_DEFAULTS = {
  land_price: 0,
  land_tax_mode: 'registration',
  land_vat_percentage: 0.21,
  land_registration_percentage: 0.12,
  construction_price: 0,
  construction_tax_mode: 'vat',
  connection_cost: 0,
  contract_cost: 0,
  vat_percentage: 0.21,
  construction_vat_percentage: 0.21,
  connection_vat_percentage: 0.21,
  contract_vat_percentage: 0.21,
  construction_registration_percentage: 0.12,
  registration_percentage: 0.12,
}


function withPriceSnapshot(unit, price = {}) {
  return {
    ...unit,
    ...PRICE_DEFAULTS,
    ...Object.fromEntries(
      Object.entries(PRICE_DEFAULTS).map(([field, fallback]) => [
        field,
        price[field] ?? fallback,
      ])
    ),
  }
}


function includesSearch(values, search) {
  return values.some(value => String(value ?? '').toLowerCase().includes(search))
}


function getProjectName(unit) {
  return unit.building_id?.project_id?.project_placeholder_name
    || unit.building_id?.project_id?.project_commercial_name
    || ''
}


export default function TransactionUnitTargets({
  selectedUnits,
  onChange,
  isBasisakte = false,
  properties = [],
  selectedProperties = [],
  loadingProperties = false,
  onSelectProperty,
}) {
  const [units, setUnits] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchUnits = async () => {
      setLoading(true)
      try {
        const response = await httpClient.post(
          import.meta.env.VITE_API_URL + '/api/units/get',
          { filters: null, search: null }
        )
        setUnits(response.data || [])
      } catch (error) {
        toast.error('Error loading units')
      } finally {
        setLoading(false)
      }
    }

    fetchUnits()
  }, [])

  const selectUnit = async (unit) => {
    if (selectedUnits.some(selected => selected.id === unit.id)) return

    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/unit-price-details/${unit.id}`
      )
      onChange([...selectedUnits, withPriceSnapshot(unit, response.data?.commercial_price)])
      setSearchValue('')
    } catch (error) {
      toast.error('Error loading unit price')
    }
  }

  const selectProperty = (property) => {
    if (selectedProperties.some(selected => selected.id === property.id)) return
    onSelectProperty?.(property)
    setSearchValue('')
  }

  const updatePrice = (unitId, field, value) => {
    onChange(selectedUnits.map(unit => (
      unit.id === unitId
        ? {
            ...unit,
            [field]: value,
            ...(field === 'vat_percentage' ? {
              construction_vat_percentage: value,
              connection_vat_percentage: value,
              contract_vat_percentage: value,
            } : {}),
            ...(field === 'registration_percentage' ? {
              land_registration_percentage: value,
              construction_registration_percentage: value,
            } : {}),
          }
        : unit
    )))
  }

  const removeUnit = (unitId) => {
    onChange(selectedUnits.filter(unit => unit.id !== unitId))
  }

  const search = searchValue.trim().toLowerCase()
  const matchingUnits = search
    ? units.filter(unit => {
        if (selectedUnits.some(selected => selected.id === unit.id)) return false
        const building = unit.building_id
        const project = building?.project_id
        return includesSearch([
          unit.unit_name,
          unit.address,
          unit.type,
          building?.building_name,
          project?.project_placeholder_name,
          project?.project_commercial_name,
        ], search)
      })
    : []

  const matchingProperties = search
    ? properties.filter(property => {
        if (selectedProperties.some(selected => selected.id === property.id)) return false

        const linkedUnits = property.property_x_unit_set?.map(link => link.unit_id).filter(Boolean) || []
        return includesSearch([
          property.cadastral_nr,
          property.partition_nr,
          formatPropertyIdentifier(property.cadastral_nr, property.partition_nr),
          property.location,
          property.function,
          ...linkedUnits.flatMap(unit => [
            unit.unit_name,
            unit.address,
            unit.building_id?.building_name,
            unit.building_id?.project_id?.project_placeholder_name,
            unit.building_id?.project_id?.project_commercial_name,
          ]),
        ], search)
      })
    : []

  const matchingTargets = [
    ...matchingProperties.map(property => ({ targetType: 'property', target: property })),
    ...matchingUnits.map(unit => ({ targetType: 'unit', target: unit })),
  ]

  return (
    <div className="transaction-unit-targets">
      <div className="search-bar--list-container">
        <input
          aria-label="Search properties and units"
          className="search-bar--list"
          type="search"
          value={searchValue}
          onChange={(event) => setSearchValue(event.currentTarget.value)}
          placeholder="Search property, unit, or project"
        />
        {searchValue && (
          <button
            aria-label="Clear target search"
            className="search-bar--clear-icon"
            type="button"
            onClick={() => setSearchValue('')}
          >
            <CloseIcon fontSize="small" />
          </button>
        )}
      </div>

      {search && (
        <div className="transaction-target-results" aria-live="polite">
          {(loading || loadingProperties) ? (
            <div className="loading__container"><CircularProgress size={30} color="inherit" /></div>
          ) : matchingTargets.length === 0 ? (
            <div className="transaction-targets__empty">No properties or units found</div>
          ) : matchingTargets.map(({ targetType, target }) => {
            if (targetType === 'unit') {
              return (
                <button
                  className="property-item transaction-target-result"
                  key={`unit-${target.id}`}
                  type="button"
                  onClick={() => selectUnit(target)}
                >
                  <span className="property_item_value transaction-target-identity">
                    <span className="transaction-target-badge unit">Unit</span>
                    <span>{target.unit_name}</span>
                  </span>
                  <span className="property_item_value">{target.address}</span>
                  <span className="property_item_value">{target.type || ''}</span>
                  <span className="property_item_value">{target.building_id?.building_name || ''}</span>
                  <span className="property_item_value">{getProjectName(target)}</span>
                  <span className="property_item__icon plus"><AddIcon fontSize="small" /></span>
                </button>
              )
            }

            const linkedUnits = target.property_x_unit_set?.map(link => link.unit_id).filter(Boolean) || []
            const unitNames = linkedUnits.map(unit => unit.unit_name).join(', ')
            const projectNames = [...new Set(linkedUnits.map(getProjectName).filter(Boolean))].join(', ')

            return (
              <button
                className="property-item transaction-target-result"
                key={`property-${target.id}`}
                type="button"
                onClick={() => selectProperty(target)}
              >
                <span className="property_item_value transaction-target-identity">
                  <span className="transaction-target-badge property">Property</span>
                  <span>{formatPropertyIdentifier(target.cadastral_nr, target.partition_nr)}</span>
                </span>
                <span className="property_item_value">{target.location}</span>
                <span className="property_item_value">{target.function}</span>
                <span className="property_item_value">{unitNames}</span>
                <span className="property_item_value">{projectNames}</span>
                <span className="property_item__icon plus"><AddIcon fontSize="small" /></span>
              </button>
            )
          })}
        </div>
      )}

      <div className="transaction-unit-selections">
        {selectedUnits.map(unit => {
          const projectName = getProjectName(unit)

          return (
            <div key={unit.id}>
              <div className="selected_property_item">
                <div className="property_item_value transaction-target-identity">
                  <span className="transaction-target-badge unit">Unit</span>
                  <span>{unit.unit_name}</span>
                </div>
                <div className="property_item_value">{unit.address}</div>
                <div className="property_item_value">{unit.type || ''}</div>
                <div className="property_item_value">{unit.building_id?.building_name || ''}</div>
                <div className="property_item_value">{projectName}</div>
                <button
                  aria-label={`Remove unit ${unit.unit_name || ''}`}
                  className="property_item__icon min"
                  type="button"
                  onClick={() => removeUnit(unit.id)}
                >
                  <RemoveIcon fontSize="small" />
                </button>
              </div>

              {!isBasisakte && (
                <div className="property-price-details">
                  <div className="price-inputs">
                    {[
                      ['land_price', 'Land'],
                      ['construction_price', 'Construction'],
                      ['connection_cost', 'Connection'],
                      ['contract_cost', 'Contract'],
                    ].map(([field, label]) => (
                      <div className="input_field--combo-grid" key={field}>
                        <label className="input_field__label">{label}</label>
                        <div className="input-with-euro">
                          <span className="euro-sign">€</span>
                          <NumericFormat
                            className="input_field__input_box--text"
                            value={unit[field]}
                            thousandSeparator=" "
                            decimalSeparator=","
                            decimalScale={2}
                            allowNegative={false}
                            onValueChange={({ floatValue }) => updatePrice(unit.id, field, floatValue ?? '')}
                          />
                        </div>
                      </div>
                    ))}

                    <div className="input_field--combo-grid small">
                      <label className="input_field__label">VAT</label>
                      <div className="input-with-euro">
                        <select
                          className="input_field__input_box--text"
                          aria-label="VAT rate"
                          value={unit.vat_percentage ?? 0.21}
                          onChange={e => updatePrice(unit.id, 'vat_percentage', Number(e.target.value))}
                        >
                          {[0, 0.06, 0.12, 0.21].map(rate => <option key={rate} value={rate}>{rate * 100}%</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="input_field--combo-grid small">
                      <label className="input_field__label">Registration</label>
                      <div className="input-with-euro">
                        <NumericFormat
                          className="input_field__input_box--text"
                          value={(unit.registration_percentage ?? 0.12) * 100}
                          decimalSeparator=","
                          decimalScale={2}
                          allowNegative={false}
                          onValueChange={({ floatValue }) => updatePrice(unit.id, 'registration_percentage', floatValue !== undefined ? floatValue / 100 : '')}
                        />
                        <span className="euro-sign">%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
